import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import CourseCard from './CourseCard';
import TestimonialCard from './TestimonialCard';
import useApi from '../hooks/useApi';
import { useAuth0 } from '@auth0/auth0-react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  Book,
  Laptop,
  Lightbulb,
  Beaker,
  Code,
  ChartColumnIncreasing,
  Brain,
  Cpu,
  Database,
  GraduationCap,
  ChartLine,
  ShieldCheck,
  Bot,
  GitGraph,
  Sparkles,
  MessageSquare,
  Blocks,
  Globe,
  Mic,
  FileText,
  Play,
  Languages,
  Users,
  Building2,
  Zap,
  Shield,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  Award,
  Star,
  Factory,
  Film
} from 'lucide-react';
import Lottie from 'react-lottie';
import LoadingScreen from './LoadingScreen';
import gif1 from '../videos/firstGIF.gif';
import animationData from '../animations/creativeAnimation1.json';
import animationDataRight from '../animations/robot.json';
import HomeFooter from './HomeFooter';
import Header from "./Header";
import Chat from './Chat';
import InstructorsPlaceholder from "./InstructorsPlaceholder";
import config from '../config/environment';
import LogoCarousel from "./LogoCarousel";

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const { fetchCourses, fetchFeaturedCourses } = useApi();
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoadingCourses(true);
        let fetchedCourses;
        if (isAuthenticated) {
          const response = await fetchCourses();
          fetchedCourses = response.courses;
        } else {
          fetchedCourses = await fetchFeaturedCourses();
        }
        setCourses(Array.isArray(fetchedCourses) ? fetchedCourses : []);
      } catch (err) {
        console.error('Error loading courses:', err);
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoadingCourses(false);
      }
    };
    loadCourses();
  }, [fetchCourses, fetchFeaturedCourses, isAuthenticated]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Success Stories Data
  const successStories = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Real-Time Translation Platform",
      description: "Enterprise-grade solution enabling financial advisors to serve clients in 20+ languages with real-time speech translation, automated documentation, and compliance recording.",
      metrics: ["20+ Languages", "Real-time Translation", "Automated Transcripts"],
      industry: "Financial Services",
      impact: "Democratized financial advisory access across linguistic barriers"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Industrial AI Knowledge Assistant",
      description: "Custom AI assistant for manufacturing, processing technical documentation with RAG architecture, providing instant answers on machinery specifications and procedures.",
      metrics: ["99% Accuracy", "10TB+ Documents", "Sub-second Response"],
      industry: "Manufacturing",
      impact: "Reduced technical query resolution time by 85%"
    },
    {
      icon: <Play className="w-8 h-8" />,
      title: "AI Video Translation Suite",
      description: "Revolutionary platform translating videos to 20 languages with voice cloning and lip-sync technology, making content appear natively produced in each language.",
      metrics: ["20 Languages", "Voice Cloning", "Perfect Lip-sync"],
      industry: "Media & Entertainment",
      impact: "Enabled global content distribution at 10x speed"
    }
  ];

  const testimonials = [
    {
      id: 1,
      quote: 'AI Guru transformed our customer service operations. Their custom AI solution reduced our response time by 80% while improving customer satisfaction scores to 4.8/5.',
      name: 'Sarah Chen',
      role: 'CTO, Global FinTech Leader',
      company: 'Fortune 500 Company',
      image: 'https://aigurufiles.s3.amazonaws.com/images/woman.webp',
    },
    {
      id: 2,
      quote: "The AI solutions delivered by AI Guru have revolutionized our manufacturing processes. We've seen a 40% increase in operational efficiency and predictive maintenance has saved us millions.",
      name: 'Michael Rodriguez',
      role: 'VP of Operations',
      company: 'Leading Industrial Manufacturer',
      image: 'https://aigurufiles.s3.amazonaws.com/images/man.webp',
    },
    {
      id: 3,
      quote: "Working with AI Guru was transformative. Their team didn't just deliver technology - they understood our business and created solutions that directly addressed our unique challenges.",
      name: 'Dr. Emily Watson',
      role: 'Chief Innovation Officer',
      company: 'Healthcare Technology Leader',
      image: 'https://aigurufiles.s3.amazonaws.com/images/woman.webp',
    },
  ];

  const iconStyle = 'text-darkgray opacity-10 transition-transform duration-300 hover:scale-110 hover:opacity-40';
  const iconPositions = [
    { Icon: Shield, style: 'top-[5%] left-[5%] rotate-[-15deg]', size: 48 },
    { Icon: Building2, style: 'top-[80%] right-[25%] rotate-[10deg]', size: 64 },
    { Icon: Zap, style: 'top-[25%] left-[20%] rotate-[5deg]', size: 40 },
    { Icon: GitGraph, style: 'top-[15%] right-[25%] rotate-[-5deg]', size: 56 },
    { Icon: Database, style: 'bottom-[20%] left-[30%] rotate-[8deg]', size: 52 },
    { Icon: Bot, style: 'top-[10%] right-[40%] rotate-[-8deg]', size: 60 },
    { Icon: Brain, style: 'top-[65%] left-[15%] rotate-[12deg]', size: 72 },
    { Icon: Cpu, style: 'bottom-[30%] right-[10%] rotate-[-10deg]', size: 58 },
    { Icon: Globe, style: 'top-[40%] left-[10%] rotate-[7deg]', size: 54 },
    { Icon: Award, style: 'bottom-[45%] right-[35%] rotate-[-6deg]', size: 68 },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AI Guru",
    "url": config.websiteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${config.websiteUrl}/courses?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <Helmet>
        <title>AI Guru - Enterprise AI Solutions | Custom AI Development & Integration</title>
        <meta name="description" content="Leading enterprise AI consultancy delivering custom solutions: Real-time translation systems, industrial AI assistants, video localization with voice cloning. Transform your business with proven AI implementations." />
        <meta name="keywords" content="Enterprise AI solutions, custom AI development, real-time translation AI, industrial AI assistant, video translation AI, voice cloning technology, RAG architecture, AI consulting, AI implementation" />
        <link rel="canonical" href={config.websiteUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div className="bg-lightgray">
        {/* Hero section - Professional Enterprise Focus */}
        <div className="relative w-full overflow-hidden py-8 sm:py-12 bg-gradient-to-br from-darkgray via-deepblue to-darkgray">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
          <div className="absolute inset-0">
            {iconPositions.map(({ Icon, style, size }, index) => (
              <Icon key={index} className={`${iconStyle} absolute ${style} text-white/5`} size={size} />
            ))}
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">
            <div className="inline-flex items-center space-x-2 bg-blue-900/30 backdrop-blur-sm border border-blue-500/20 rounded-full px-6 py-2 mb-6 mt-16 sm:mt-20">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-300">Trusted by Fortune 500 Companies</span>
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight sm:leading-normal max-w-5xl mx-auto py-4">
              Enterprise AI Solutions
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                That Transform Industries
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              From real-time translation platforms to industrial AI assistants, we build custom AI solutions 
              that solve complex business challenges and drive measurable results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/solution"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:opacity-90 transition duration-300 flex items-center justify-center group"
              >
                Explore Our Solutions
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contactUs"
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-bold py-3 px-8 rounded-lg hover:bg-white/20 transition duration-300"
              >
                Schedule Consultation
              </Link>
            </div>
            {/* Trust Indicators */}
            <div className="mt-12 grid grid-cols-3 gap-8 text-center max-w-3xl mx-auto">
              <div>
                <div className="text-3xl font-bold text-white">100+</div>
                <div className="text-sm text-gray-400">Enterprise Implementations</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">20+</div>
                <div className="text-sm text-gray-400">Countries Served</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">95%</div>
                <div className="text-sm text-gray-400">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Stories Section - Showcasing Real Solutions */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Proven AI Solutions in Production
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Real implementations delivering transformative results across industries
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {successStories.map((story, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center text-white mb-6">
                    {story.icon}
                  </div>
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-4">
                    {story.industry}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{story.title}</h3>
                  <p className="text-gray-600 mb-6">{story.description}</p>
                  <div className="space-y-2 mb-6">
                    {story.metrics.map((metric, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                        <span>{metric}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-6 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-900">Business Impact:</p>
                    <p className="text-sm text-gray-600 mt-1">{story.impact}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/solution"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium group"
              >
                View all case studies
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Core Capabilities Section */}
        <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Our Core Capabilities
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive AI expertise to tackle your most complex challenges
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Custom AI Development */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white mb-4">
                  <Code className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Custom AI Development</h3>
                <p className="text-gray-600">
                  Tailored AI solutions designed specifically for your business needs and workflows
                </p>
              </div>
              
              {/* Enterprise Integration */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center text-white mb-4">
                  <GitGraph className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Enterprise Integration</h3>
                <p className="text-gray-600">
                  Seamless integration with existing systems using MCP and modern architectures
                </p>
              </div>
              
              {/* AI Transformation */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-teal-600 rounded-lg flex items-center justify-center text-white mb-4">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">AI Transformation</h3>
                <p className="text-gray-600">
                  End-to-end digital transformation powered by cutting-edge AI technologies
                </p>
              </div>
              
              {/* Ongoing Support */}
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg flex items-center justify-center text-white mb-4">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">24/7 Support & Optimization</h3>
                <p className="text-gray-600">
                  Continuous monitoring, optimization, and support for all deployed solutions
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Technology Stack Section - Layered Architecture */}
        <div className="py-20 bg-darkgray text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Enterprise AI Architecture
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Our modular, scalable architecture adapts to your infrastructure and requirements
              </p>
            </div>
            
            {/* Layered Architecture Visualization */}
            <div className="space-y-6">
              {/* Application Layer */}
              <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-8 border border-blue-500/20">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Blocks className="w-6 h-6 mr-3 text-blue-400" />
                  Application Layer
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <Globe className="w-8 h-8 mx-auto mb-2 text-blue-300" />
                    <div className="font-medium">Real-time Translation</div>
                    <div className="text-sm text-gray-400">Multi-language support</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <Brain className="w-8 h-8 mx-auto mb-2 text-purple-300" />
                    <div className="font-medium">AI Assistants</div>
                    <div className="text-sm text-gray-400">Intelligent automation</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <Film className="w-8 h-8 mx-auto mb-2 text-green-300" />
                    <div className="font-medium">Video Processing</div>
                    <div className="text-sm text-gray-400">Voice cloning & lip-sync</div>
                  </div>
                </div>
              </div>

              {/* AI Core Layer */}
              <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Brain className="w-6 h-6 mr-3 text-purple-400" />
                  AI Core Engine
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <div className="font-medium text-sm">OpenAI GPT</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <div className="font-medium text-sm">Anthropic Claude</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <div className="font-medium text-sm">Mistral AI</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <div className="font-medium text-sm">Meta LLaMA</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center hover:bg-white/20 transition">
                    <div className="font-medium text-sm">DeepSeek</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <Database className="w-6 h-6 mx-auto mb-2 text-indigo-300" />
                    <div className="text-sm">RAG Architecture</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <GitGraph className="w-6 h-6 mx-auto mb-2 text-indigo-300" />
                    <div className="text-sm">MCP Protocol</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <Cpu className="w-6 h-6 mx-auto mb-2 text-indigo-300" />
                    <div className="text-sm">Vector Databases</div>
                  </div>
                </div>
              </div>

              {/* Infrastructure Layer */}
              <div className="bg-gradient-to-r from-green-900/30 to-teal-900/30 backdrop-blur-sm rounded-2xl p-8 border border-green-500/20">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Shield className="w-6 h-6 mr-3 text-green-400" />
                  Infrastructure & Security
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3 text-green-300">Cloud Platforms</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white/10 rounded-lg p-3 text-center hover:bg-white/20 transition">
                        <div className="text-sm">AWS</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3 text-center hover:bg-white/20 transition">
                        <div className="text-sm">Azure</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3 text-center hover:bg-white/20 transition">
                        <div className="text-sm">GCP</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-3 text-green-300">Deployment Options</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/10 rounded-lg p-3 text-center hover:bg-white/20 transition">
                        <div className="text-sm">On-Premise</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-3 text-center hover:bg-white/20 transition">
                        <div className="text-sm">Hybrid Cloud</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center space-x-8 text-sm text-gray-400">
                  <div className="flex items-center">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-400" />
                    SOC 2 Compliant
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-400" />
                    GDPR Ready
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-400" />
                    HIPAA Compliant
                  </div>
                </div>
              </div>
            </div>

            {/* Architecture Benefits */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-blue-400" />
                </div>
                <h4 className="font-medium mb-2">Modular Design</h4>
                <p className="text-sm text-gray-400">Swap LLMs and components without rebuilding</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-purple-400" />
                </div>
                <h4 className="font-medium mb-2">Enterprise Security</h4>
                <p className="text-sm text-gray-400">Multi-layer security with compliance built-in</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <h4 className="font-medium mb-2">Infinite Scale</h4>
                <p className="text-sm text-gray-400">From pilot to enterprise deployment seamlessly</p>
              </div>
            </div>
          </div>
        </div>

        {/* Client Testimonials - Enterprise Focus */}
        <div className="py-20 bg-gradient-to-r from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                What Industry Leaders Say
              </h2>
              <p className="text-xl text-gray-600">
                Trusted by enterprises worldwide to deliver transformative AI solutions
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-3">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic mb-6">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    <img
                      className="h-12 w-12 rounded-full mr-4"
                      src={testimonial.image}
                      alt={testimonial.name}
                    />
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                      <p className="text-xs text-gray-500">{testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Our Proven Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From concept to deployment in weeks, not months
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "1", title: "Discovery", description: "Deep dive into your challenges and objectives", time: "Week 1" },
                { step: "2", title: "Architecture", description: "Design custom AI solution architecture", time: "Week 2-3" },
                { step: "3", title: "Development", description: "Build and test your AI solution", time: "Week 4-8" },
                { step: "4", title: "Deployment", description: "Launch, monitor, and optimize", time: "Week 9+" }
              ].map((phase, index) => (
                <div key={index} className="relative">
                  <div className="bg-gray-50 rounded-xl p-6 h-full border-2 border-gray-100 hover:border-blue-500 transition-colors">
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                      {phase.step}
                    </div>
                    <div className="mt-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.title}</h3>
                      <p className="text-gray-600 mb-4">{phase.description}</p>
                      <div className="text-sm text-blue-600 font-medium">{phase.time}</div>
                    </div>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Training Section - Positioned as Complementary */}
        <div className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Empower Your Team with AI Knowledge
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Complement your AI implementation with world-class training programs
              </p>
            </div>
            {loadingCourses ? (
              <p className="text-center">Loading training programs...</p>
            ) : error ? (
              <p className="text-center text-red-500">{error}</p>
            ) : courses.length > 0 ? (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {courses.slice(0, 3).map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
            ) : (
              <p className="text-center">No training programs available at the moment.</p>
            )}
            <div className="mt-12 text-center">
              <Link
                to="/courses"
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                View All Training Programs
              </Link>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="max-w-4xl mx-auto text-center py-16 px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white">
              <span className="block">Ready to Transform Your Business with AI?</span>
            </h2>
            <p className="mt-4 text-xl text-blue-100">
              Join industry leaders who've revolutionized their operations with our custom AI solutions.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contactUs"
                className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-100 transition group"
              >
                Schedule Enterprise Consultation
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solution"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition"
              >
                Explore Our Solutions
              </Link>
            </div>
            <div className="mt-8 text-blue-200">
              <p className="text-sm">
                Typical ROI: 300%+ within 12 months • Implementation: 4-12 weeks
              </p>
            </div>
          </div>
        </div>

        {/* Partnership Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Trusted Technology Partners
              </h2>
              <p className="text-xl text-gray-600">
                We work with leading technology providers to deliver best-in-class solutions
              </p>
            </div>
            <LogoCarousel />
          </div>
        </div>

        {/* Back to Top button */}
        {showScroll && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-16 right-32 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition duration-300 z-50"
            aria-label="Back to top"
          >
            ↑
          </button>
        )}

        <HomeFooter />
        <Chat />
      </div>
    </>
  );
};

export default Home;
